'use client';

import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useAddToCart } from '../../../cart/hooks';
import { useMaxQuantity } from '../../../lib/hooks/use-max-quantity';
import { useHasWastageState } from './hooks';
import { ProductQuantityShell } from './product-quantity-shell';

interface ProductQuantityProps {
  domain: string;
  productType: 'TILE' | string;
  squareMetersPerBox: number | null;
  productGid: string | null;
  variantGid: string | null;
  allowBackorder?: boolean;
  wastageSwitch?: ReactNode;
  className?: string;
  containerClassName?: string;
  quantityInputClassName?: string;
  hintClassName?: string;
}

export function ProductQuantity({
  domain,
  productType,
  squareMetersPerBox,
  productGid,
  variantGid,
  allowBackorder = false,
  wastageSwitch,
  className = 'col-span-full',
  containerClassName = 'grid-cols-2 gap-[--product-grid-gap] max-sm:flex sm:grid',
  quantityInputClassName = 'flex w-full select-none items-center border border-primary/20 px-4 py-[14px] max-md:text-sm',
  hintClassName = 'mt-0.5 text-xs',
}: ProductQuantityProps) {
  const isTile = productType === 'TILE';
  const { hasWastage } = useHasWastageState();
  const maxQuantity = useMaxQuantity({
    productGid,
    variantGid,
    allowBackorder,
  });
  const {
    setQuantity: setCartQuantity,
    quantity,
    setQuantity,
  } = useAddToCart({ domain });
  const [intendedArea, setIntendedArea] = useState(1);
  const wastageMultiplier = hasWastage ? 1.15 : 1;

  // For tiles, calculate max area based on available boxes
  const maxArea =
    isTile && squareMetersPerBox && maxQuantity
      ? (maxQuantity * squareMetersPerBox) / wastageMultiplier
      : undefined;

  useEffect(() => {
    if (quantity === 1 && isTile) {
      setIntendedArea(1);
    }
  }, [quantity, isTile]);

  useEffect(() => {
    if (isTile && intendedArea > 0 && squareMetersPerBox) {
      const boxesNeeded = Math.ceil(
        (intendedArea * wastageMultiplier) / squareMetersPerBox
      );
      setCartQuantity(boxesNeeded);
    }
  }, [
    intendedArea,
    wastageMultiplier,
    squareMetersPerBox,
    setCartQuantity,
    isTile,
  ]);

  const handleAreaChange = (area: number) => {
    setIntendedArea(area);
    if (squareMetersPerBox) {
      const boxesNeeded = Math.ceil(
        (area * wastageMultiplier) / squareMetersPerBox
      );
      setCartQuantity(boxesNeeded);
    }
  };

  return (
    <ProductQuantityShell
      productType={productType}
      squareMetersPerBox={squareMetersPerBox}
      isStatic={false}
      className={className}
      containerClassName={containerClassName}
      quantityInputClassName={quantityInputClassName}
      hintClassName={hintClassName}
      quantity={isTile ? intendedArea : quantity}
      maxQuantity={isTile ? maxArea : maxQuantity}
      onQuantityChange={isTile ? handleAreaChange : setQuantity}
      wastageSwitch={wastageSwitch}
    />
  );
}
